import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Box,
  TextareaAutosize,
  CircularProgress,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useSelector } from "react-redux";
import ApiService from "../../utils/fetch";
import {
  getMenuListApplicableForSupport,
  getCategoryList,
  getFunctionalityList,
  getApplicableAdditionalFields,
  getSubmenuListForMenu,
} from "./SupportTicketHelperUtil";
import { useSupport } from "../../context/SupportContext";
import ChipListBox from "./ChipListBox";
import ChipDialog from "./ChipDialog";

const SupportTicketDialog = ({
  currentMenu,
  currentParentMenu,
  open,
  onClose,
}) => {
  let {
    combinationDetails,
    card1CombinationDetails,
    card2CombinationDetails,
    popupCombinationDetails,
    bmfCard1CombinationDetails,
    bmfCard2CombinationDetails,
    bmfPopupCombinationDetails,
    fgCard1CombinationDetails,
    fgCard2CombinationDetails,
    fgPopupCombinationDetails,
    bpCard1CombinationDetails,
    bpCard2CombinationDetails,
    bpPopupCombinationDetails,
    setCombinationDetails,
    ciCard1CombinationDetails,
    ciCard2CombinationDetails,
    ciPopupCombinationDetails,
    prCard1CombinationDetails,
    prCard2CombinationDetails,
    prPopupCombinationDetails,
  } = useSupport();

  const [formData, setFormData] = useState({
    menu: "",
    subMenu: "",
    category: "",
    functionality: "",
    description: "",
    initiator: "",
    assignedTo: "",
    urgency: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedBoxDetails, setSelectedBoxDetails] = useState({
    mappedData: {},
  });
  const [openBoxModal, setOpenBoxModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [functionalityOptions, setFunctionalityOptions] = useState([]);
  const [assignedToOptions, setAssignedToOptions] = useState(["AL Team"]);
  const [urgencyOptions, setUrgencyOptions] = useState(["Standard", "Urgent"]);
  const [additionalFields, setAdditionalFields] = useState({
    bottler: false,
    forecastPartner: false,
    plant: false,
    profitCenter: false,
  });

  const email = useSelector((state) => state.user.email);
  const dynamicRoutes = useSelector((state) => state.user.routes);
  console.log(dynamicRoutes, "dynamicRoutes")

  const initiatorOptions = [email];
  const menuOptions = getMenuListApplicableForSupport(dynamicRoutes);

  const isBCFMenu = formData.menu === "Bottler Concentrate Forecast";

  const isProfitCenterMenu =
    formData.menu === "Bottler Market Forecast" ||
    formData.menu === "FG Inventory" ||
    formData.menu === "Bottler Business Plan" ||
    formData.menu === "Concentrate Inventory" ||
    formData.menu === "Production Requirement";

  const isOtherMenu = !isBCFMenu && !isProfitCenterMenu;

  useEffect(() => {
    if (formData.menu) {
      setCategoryOptions(getCategoryList(formData.menu, formData.subMenu));
    } else {
      setCategoryOptions([]);
    }
    setFormData((prev) => ({ ...prev, category: "", functionality: "" }));
  }, [formData.menu, formData.subMenu]);

  // const submenuOptions = useMemo(() => {
  //   const menu = dynamicRoutes.find((m) => m.menuDisplayName === formData.menu);
  //   return menu ? menu.submenuList.map((sub) => sub.submenuDisplayName) : [];
  // }, [dynamicRoutes, formData.menu]);

  const submenuOptions = useMemo(
    () => getSubmenuListForMenu(dynamicRoutes, formData.menu),
    [dynamicRoutes, formData.menu],
  );

  useEffect(() => {
    const fields = getApplicableAdditionalFields(
      formData.menu,
      formData.subMenu,
    );

    setAdditionalFields(fields);
  }, [formData.menu, formData.subMenu]);

  const distinctBottlers = useMemo(() => {
    if (!combinationDetails?.length) return [];
    return [
      ...new Set(
        combinationDetails
          .filter((item) => item.bottler?.trim())
          .map((item) => item.bottler),
      ),
    ];
  }, [combinationDetails]);

  const distinctCustomers = useMemo(() => {
    if (!combinationDetails?.length) return [];
    return [
      ...new Set(
        combinationDetails
          .filter((item) => item.customerName?.trim())
          .map((item) => item.customerName),
      ),
    ];
  }, [combinationDetails]);

  const distinctBusinessCodes = useMemo(() => {
    if (!combinationDetails?.length) return [];
    return [
      ...new Set(
        combinationDetails
          .filter((item) => item.bottlerOwnerCode?.trim())
          .map((item) => item.bottlerOwnerCode),
      ),
    ];
  }, [combinationDetails]);

  const distinctLocations = useMemo(() => {
    if (!combinationDetails?.length) return [];
    return [
      ...new Set(
        combinationDetails
          .filter((item) => item.locationName?.trim())
          .map((item) => item.locationName),
      ),
    ];
  }, [combinationDetails]);

  const distinctProfitCenterCountries = useMemo(() => {
    if (!combinationDetails?.length) return [];

    return [
      ...new Set(
        combinationDetails
          .filter((item) => item.profitCenterCountry?.trim())
          .map((item) => item.profitCenterCountry),
      ),
    ];
  }, [combinationDetails]);

  useEffect(() => {
    if (formData.menu && formData.category) {
      // Load functionality options for the selected menu + category
      setFunctionalityOptions(
        getFunctionalityList(
          formData.menu,
          formData.subMenu,
          formData.category,
        ),
      );
    } else {
      // Clear functionality options and value
      setFunctionalityOptions([]);
    }
    setFormData((prev) => ({ ...prev, functionality: "" }));
  }, [formData.menu, formData.subMenu, formData.category]);

  const DASHBOARD_CATEGORY_MAP = {
    "Bottler Concentrate Forecast": {
      "Forecast Upload Status": card1CombinationDetails,
      "Previous Forecast Uploads": card2CombinationDetails,
      "Record detail": popupCombinationDetails,
    },

    "Bottler Market Forecast": {
      "Forecast Upload Status": bmfCard1CombinationDetails,
      "Previous Forecast Uploads": bmfCard2CombinationDetails,
      "Record detail": bmfPopupCombinationDetails,
    },

    "FG Inventory": {
      "FG Inventory Upload Status": fgCard1CombinationDetails,
      "Previous FG Inventory Uploads": fgCard2CombinationDetails,
      "Record detail": fgPopupCombinationDetails,
    },

    "Bottler Business Plan": {
      "Bottler Business Plan Upload Status": bpCard1CombinationDetails,
      "Previous Bottler Business Plan Uploads": bpCard2CombinationDetails,
      "Record detail": bpPopupCombinationDetails,
    },

    "Concentrate Inventory": {
      "Concentrate Inventory Upload Status": ciCard1CombinationDetails,
      "Previous Concentrate Inventory Uploads": ciCard2CombinationDetails,
      "Record detail": ciPopupCombinationDetails,
    },

    "Production Requirement": {
      "Production Requirement Upload Status": prCard1CombinationDetails,
      "Previous Production Requirement Uploads": prCard2CombinationDetails,
      "Record detail": prPopupCombinationDetails,
    },
  };

  useEffect(() => {
    if (currentMenu !== "Dashboard") {
      return;
    }

    const parentMap = DASHBOARD_CATEGORY_MAP[currentParentMenu];

    if (!parentMap) {
      setCombinationDetails([]);
      return;
    }

    const dataset = parentMap[formData.category];

    setCombinationDetails(Array.isArray(dataset) ? dataset : []);
  }, [
    currentMenu,
    currentParentMenu,
    formData.category,
    card1CombinationDetails,
    card2CombinationDetails,
    popupCombinationDetails,
    bmfCard1CombinationDetails,
    bmfCard2CombinationDetails,
    bmfPopupCombinationDetails,
    fgCard1CombinationDetails,
    fgCard2CombinationDetails,
    fgPopupCombinationDetails,
    bpCard1CombinationDetails,
    bpCard2CombinationDetails,
    bpPopupCombinationDetails,
    ciCard1CombinationDetails,
    ciCard2CombinationDetails,
    ciPopupCombinationDetails,
    prCard1CombinationDetails,
    prCard2CombinationDetails,
    prPopupCombinationDetails,
  ]);

  useEffect(() => {
    if (open) {
      setFormData({
        menu: currentParentMenu || "",
        subMenu: currentParentMenu === currentMenu ? "" : currentMenu || "",
        category: "",
        functionality: "",
        description: "",
        initiator: email,
        assignedTo: "AL Team",
        urgency: "Standard",
      });
      setSuccessMessage("");
    }
  }, [open, currentParentMenu, currentMenu]);

  const handleChange = (field, maxLength) => (e) => {
    const { value } = e.target;
    if (value.length <= maxLength) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const isFormValid =
    formData.menu.trim() &&
    //formData.title.trim() &&
    formData.description.trim() &&
    formData.initiator.trim() &&
    formData.assignedTo.trim() &&
    formData.urgency.trim();

  const createTicketAPI = async () => {
    const description = `${formData.description || ""}${combinationDetails && combinationDetails.length
        ? ` (Combinations are ${combinationDetails
          .map((item) => {
            const parts = [];
            if (item.bottler) parts.push(`Bottler: ${item.bottler}`);

            if (isBCFMenu) {
              if (item.customerName)
                parts.push(`Forecast Partner: ${item.customerName}`);
              if (item.locationName)
                parts.push(`Plant: ${item.locationName}`);
            }

            if (isProfitCenterMenu) {
              if (item.bottlerOwnerCode)
                parts.push(`Business Code: ${item.bottlerOwnerCode}`);
              if (item.profitCenterCountry)
                parts.push(`Country: ${item.profitCenterCountry}`);
            }
            return parts.join(", ");
          })
          .filter(Boolean)
          .join(" | ")})`
        : ""
      }`;
    const payload = {
      //title: formData.title,
      menu: formData.menu,
      submenu: formData.subMenu,
      description: description,
      initiator: formData.initiator,
      category: formData.category,
      functionality: formData.functionality,
      assignedTo: formData.assignedTo,
      urgency: formData.urgency,
    };

    try {
      setLoading(true);
      await ApiService.post("/v1/dynamic-ticket/create-ticket", payload);
      setSuccessMessage("Ticket created successfully!");
      setLoading(false);
      onClose();
    } catch (err) {
      console.error(err);
      setSuccessMessage("Failed to create ticket.");
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setSuccessMessage("");
    onClose();
  };

  const handleChipClick = (mappedData, name) => {
    setSelectedBoxDetails({ mappedData, name });
    setOpenBoxModal(true);
  };

  const handleCloseBoxModel = () => {
    setOpenBoxModal(false);
    setSelectedBoxDetails({ mappedData: [] });
  };

  return (
    <>
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Support Ticket</Typography>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={menuOptions}
                value={formData.menu}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    menu: newValue || "",
                    subMenu: "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Menu"
                    size="small"
                    required
                    fullWidth
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 39, // match Initiator height
                        paddingRight: 0, // optional: adjust for dropdown arrow
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 12px", // same padding as Autocomplete
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={submenuOptions}
                value={formData.subMenu || ""}
                getOptionLabel={(option) => option || ""}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    subMenu: newValue || "",
                  }))
                }
                // disabled={formData.subMenu === ""}
                disabled={!formData.menu}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Submenu"
                    size="small"
                    required
                    fullWidth
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 39, // match Initiator height
                        paddingRight: 0, // optional: adjust for dropdown arrow
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 12px", // same padding as Autocomplete
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={categoryOptions}
                value={formData.category}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, category: newValue || "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 39, // match Initiator height
                        paddingRight: 0, // optional: adjust for dropdown arrow
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 12px", // same padding as Autocomplete
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={functionalityOptions}
                value={formData.functionality}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, functionality: newValue || "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Functionality"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 39, // match Initiator height
                        paddingRight: 0, // optional: adjust for dropdown arrow
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 12px", // same padding as Autocomplete
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label className="label-position" htmlFor="hierarchy">
                Bottler
              </label>
              <ChipListBox
                mappedData={distinctBottlers}
                name="Bottler"
                onClick={handleChipClick}
                disabled={!additionalFields.bottler}
              />
            </Grid>

            {(isBCFMenu || isOtherMenu) && (
              <>
                <Grid item xs={12} md={3}>
                  <label className="label-position">Forecast Partner</label>
                  <ChipListBox
                    mappedData={distinctCustomers}
                    name="Forecast Partner"
                    onClick={handleChipClick}
                    disabled={!additionalFields.forecastPartner}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <label className="label-position">Plant</label>
                  <ChipListBox
                    mappedData={distinctLocations}
                    name="Plant"
                    onClick={handleChipClick}
                    disabled={!additionalFields.plant}
                  />
                </Grid>
              </>
            )}

            {(isProfitCenterMenu || isOtherMenu) && (
              <>
                <Grid item xs={12} md={3}>
                  <label className="label-position">Bottler Owner Code</label>
                  <ChipListBox
                    mappedData={distinctBusinessCodes}
                    name="Bottler Owner Code"
                    onClick={handleChipClick}
                    disabled={!additionalFields.profitCenter}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <label className="label-position">Country</label>
                  <ChipListBox
                    mappedData={distinctProfitCenterCountries}
                    name="Country"
                    onClick={handleChipClick}
                    disabled={!additionalFields.profitCenter}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={3} mt={3}>
              <Autocomplete
                options={urgencyOptions}
                value={formData.urgency}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, urgency: newValue || "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Urgency"
                    size="small"
                    required
                    fullWidth
                    autoComplete="off"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 39, // match Initiator height
                        paddingRight: 0, // optional: adjust for dropdown arrow
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 12px", // same padding as Autocomplete
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextareaAutosize
                minRows={6}
                placeholder="Issue Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
                autoComplete="off" // prevent autofill
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          {/* Initiator Autocomplete on the left */}
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            <Autocomplete
              options={initiatorOptions}
              value={formData.initiator}
              onChange={(e, newValue) =>
                setFormData({ ...formData, initiator: newValue || "" })
              }
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Initiator"
                  size="small"
                  autoComplete="off"
                />
              )}
            />

            <Autocomplete
              options={assignedToOptions}
              value={formData.assignedTo}
              onChange={(e, newValue) =>
                setFormData({ ...formData, assignedTo: newValue || "" })
              }
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned To"
                  size="small"
                  required
                  autoComplete="off"
                />
              )}
            />
          </Box>

          {/* Buttons on the right */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={onCancel}
              sx={{
                color: "black",
                backgroundColor: "white",
                textTransform: "capitalize",
                height: "30px",
                maxWidth: "150px",
                width: "100%",
                borderRadius: "6px",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "white", // keep inside white
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.5)", // outer shadow
                  borderColor: "black", // keep border same
                },
                "&:focus": { boxShadow: "0px 2px 8px rgba(0,0,0,0.5)" }, // remove blue focus
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={createTicketAPI}
              disabled={!isFormValid || loading}
              sx={{
                backgroundColor: "black",
                color: "white",
                textTransform: "capitalize",
                height: "30px",
                maxWidth: "150px",
                width: "100%",
                borderRadius: "6px",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "black", // keep inside black
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.5)", // outer shadow
                },
                "&:focus": { boxShadow: "0px 2px 8px rgba(0,0,0,0.5)" }, // remove focus blue
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      {/* View All Chips Dialog */}
      <ChipDialog
        open={openBoxModal}
        onClose={handleCloseBoxModel}
        name={selectedBoxDetails?.name}
        mappedData={selectedBoxDetails?.mappedData}
      />
    </>
  );
};

export default SupportTicketDialog;
